// JUST FIREBASE LOGIC, HANDLES HTTP/FIREBASE REQUESTS
// IMPORTANT	--	NO MERGE WITH REDUX LOGIC --

import { collection, doc, getDocs, query, updateDoc, where, getDoc, Timestamp, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { formatTicketData } from '../../utils/helpers/reports/helperRevenueReport';
import { convertTimestampsToUnix } from '../../utils/dateUtils';

export const getDailyTickets = async (vid) => {
  try {
    const q = query(collection(db, 'dailyTickets'), where('vid', '==', vid));
    const querySnapshot = await getDocs(q);
    const tickets = querySnapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...convertTimestampsToUnix(docSnap.data()),
    }));
    return tickets;
  } catch (error) {
    console.error('Error fetching daily tickets:', error);
    throw new Error(`Error fetching daily tickets: ${error.message}`);
  }
};

export const getDailyTicketByDate = async (vid, dateString) => {
  try {
    const ticketId = `${vid}_${dateString}`;
    const ticketRef = doc(db, 'dailyTickets', ticketId);
    const ticketSnap = await getDoc(ticketRef);

    if (!ticketSnap.exists()) {
      return null;
    }

    return {
      id: ticketSnap.id,
      ...convertTimestampsToUnix(ticketSnap.data()),
    };
  } catch (error) {
    console.error('Error fetching daily ticket by date:', error);
    throw new Error(`Error fetching daily ticket by date: ${error.message}`);
  }
};

export const getDailyTicketsByPeriod = async (vid, period = null) => {
  // Validate venue ID
  if (!vid) {
    console.error('No venue ID provided for fetching daily tickets');
    throw new Error('Venue ID is required');
  }

  try {
    let q;

    if (period && period.start && period.end) {
      // Convert ISO dates to Firestore Timestamp
      const startTimestamp = Timestamp.fromDate(new Date(period.start));
      const endTimestamp = Timestamp.fromDate(new Date(period.end));

      q = query(
        collection(db, 'dailyTickets'),
        where('vid', '==', vid),
        where('date', '>=', startTimestamp),
        where('date', '<=', endTimestamp)
      );
    } else {
      // If no period is provided, just query by venue ID
      q = query(collection(db, 'dailyTickets'), where('vid', '==', vid));
    }

    const querySnapshot = await getDocs(q);

    const tickets = querySnapshot.docs.map((docSnap) => formatTicketData(docSnap.data(), docSnap.id));

    return tickets;
  } catch (error) {
    console.error('Error fetching daily tickets:', error);
    throw new Error(`Error fetching daily tickets: ${error.message}`);
  }
};

export const updateDailyTicketNote = async (ticketId, fieldName, noteText) => {
  // Validate parameters
  if (!ticketId) {
    throw new Error('Ticket ID is required to update a note');
  }

  if (!fieldName) {
    throw new Error('Field name is required to update a note');
  }

  try {
    const ticketRef = doc(db, 'dailyTickets', ticketId);

    // Validate that the ticket exists
    const existingTicketSnap = await getDoc(ticketRef);
    if (!existingTicketSnap.exists()) {
      throw new Error('Ticket not found in Firestore');
    }

    // Update the ticket with the new note
    await updateDoc(ticketRef, { [fieldName]: noteText });

    // Get the updated ticket
    const updatedTicketSnap = await getDoc(ticketRef);
    return formatTicketData(updatedTicketSnap.data(), updatedTicketSnap.id);
  } catch (error) {
    console.error('Failed to update ticket note:', error);
    throw new Error(`Error updating ticket note: ${error.message}`);
  }
};

export const subscribeToDailyTickets = (venueId, period, onUpdate) => {
  if (!venueId || !period?.start || !period?.end) return () => {};

  const startDate = new Date(period.start);
  const endDate = new Date(period.end);

  const dailyTicketsQuery = query(
    collection(db, 'dailyTickets'),
    where('vid', '==', venueId),
    where('date', '>=', startDate),
    where('date', '<=', endDate)
  );

  const unsubscribe = onSnapshot(dailyTicketsQuery, (snapshot) => {
    const dailyTickets = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...convertTimestampsToUnix(doc.data()),
    }));
    onUpdate(dailyTickets);
  });

  return unsubscribe;
};

export const subscribeToDailyTicketById = (ticketId, onUpdate) => {
  if (!ticketId) return () => {};

  const ticketRef = doc(db, 'dailyTickets', ticketId);

  const unsubscribe = onSnapshot(
    ticketRef,
    (snapshot) => {
      if (snapshot.exists()) {
        const ticket = {
          id: snapshot.id,
          ...convertTimestampsToUnix(snapshot.data()),
        };
        onUpdate(ticket);
      } else {
        onUpdate(null);
      }
    },
    (error) => {
      console.error('Error subscribing to daily ticket:', error);
      onUpdate(null);
    }
  );

  return unsubscribe;
};

export const updateDailyTicketNotes = async (vid, dateString, noteType, noteText) => {
  try {
    const ticketId = `${vid}_${dateString}`;
    const ticketRef = doc(db, 'dailyTickets', ticketId);

    // Create the field name based on note type
    const fieldName = `${noteType}Notes`; // Will be either 'employeeNotes' or 'managerNotes'

    // Update the document
    await updateDoc(ticketRef, {
      [fieldName]: noteText,
      updatedAt: Timestamp.now(),
    });

    // Get the updated document
    const updatedDoc = await getDoc(ticketRef);
    if (!updatedDoc.exists()) {
      throw new Error('Ticket not found after update');
    }

    // Format the data before returning using convertTimestampsToUnix
    return {
      id: updatedDoc.id,
      ...convertTimestampsToUnix(updatedDoc.data()),
    };
  } catch (error) {
    console.error('Error updating daily ticket notes:', error);
    throw new Error(`Error updating daily ticket notes: ${error.message}`);
  }
};
